import './kp-main-menu.less';
import template from './kp-main-menu.tpl.pug';
import bind from 'bind-decorator';

// @formatter:off
/**
 * @ngdoc component
 * @name kpMainMenu
 * @module portaro.components.main-menu
 *
 * @requires https://docs.angularjs.org/api/ngAnimate ngAnimate
 * @requires https://angular-ui.github.io/bootstrap ui.bootstrap
 *
 * @param {Array<menuItem>} menuItems Menu definition
 * @param {string} searchPlaceholder Placeholder in search input
 *
 * @description
 * Component for main menu. Menu is stickable on top of page.
 *
 * Transcluding
 * ============
 *
 * | Param          | Desc           |
 * |----------------|----------------|
 * | kpMainMenuLogo | Logo in navbar |
 * | kpMainMenuCustomContent | Custom content, which can be inserted on the right end of menu |
 *
 * Styling
 * =========
 * Default styles for kp-main-menu (in LESS):
 * ```css
 * .kp-main-menu {
 *     // Background color for all menu
 *     background-color: #F8F8F8;
 *     // text color of all menu
 *     color: #777;
 *     text-transform: uppercase;
 *     border-bottom: 1px solid #e7e7e7;
 *     border-top: 1px solid #e7e7e7;
 *
 *      // Set :hover style. !important keyword is important
 *      .onhover {
 *          color: #333 !important;
 *          background-color: #E7E7E7 !important;
 *      }
 *
 *      // styles for search input
 *      .search {
 *          color: #555;
 *          background-color: white;
 *      }
 *
 *      // style for side-menu
 *      .sidemenu-separator {
 *          border-top: 1px solid #e7e7e7;
 *      }
 * }
 * ```
 *
 * @example
 * <example module="mainMenuExample" name="mainMenuExample" frame-no-resize="true">
        <file name="index.html">
            <div class="spacer1"></div>
            <main ng-controller="menuExampleController as mec">
                <kp-main-menu menu-items="mec.menuItems"></kp-main-menu>
            </main>
            <div class="spacer2"></div>
        </file>
        <file name="script.js">
            class MenuExampleController {
                constructor() {
                    this.menuItems = [
                        {
                            text: 'Home',
                            requiresAuth: false
                        },
                        {
                            text: 'Submenu',
                            submenu: [
                                {
                                    text: 'Submenu 1'
                                },
                                {
                                    text: 'Submenu 2'
                                },
                                {
                                    text: 'Submenu 3'
                                }
                            ]
                        },
                        {
                            text: 'My Account',
                            requiresAuth: true
                        },
                        {
                            text: 'Sign In',
                            type: 'user',
                            requiresAuth: true
                        }
                    ];
                }
            }

            mainMenuConfig.$inject = ['mainMenuServiceProvider'];
            function mainMenuConfig(mainMenuServiceProvider) {
                // user is not logged
                mainMenuServiceProvider.setIsLoggedFunction(() => false);

                mainMenuServiceProvider.setOnRequireAuthClick((menuItem) => {
                    alert(`You don't have permissions for page ${menuItem.text}`);
                });

                mainMenuServiceProvider.setSearchFunction((searchPhrase) => {
                    alert(`Search for ${searchPhrase}`);
                });
            }

            angular.module('mainMenuExample', ['portaro'])
                .controller('menuExampleController', MenuExampleController)
                .config(mainMenuConfig);
        </file>
        <file name="styles.css">
            .spacer1 {
                width: 100%;
                height: 100px;
            }

            .spacer2 {
                width: 100%;
                height: 500px;
            }
        </file>
 * </example>
 */
// @formatter:on

class MainMenuComponentController {

    isSticked = false;
    searchActive = false;
    searchString = '';
    renderLogoContainer = false;
    renderCustomContentContainer = false;

    /*@ngInject*/
    constructor($scope, $element, $transclude, mainMenuService, searchInputService) {
        this.$scope = $scope;
        this.$transclude = $transclude;
        this.mainMenuService = mainMenuService;
        this.searchInputService = searchInputService;
        $element.addClass('kp-main-menu');
    }

    $postLink() {
        this.userMenu = this.menuItems.filter((e) => e.type === 'user')[0];
        this.renderLogoContainer = this.$transclude.isSlotFilled('mainMenuLogo');
        this.renderCustomContentContainer = this.$transclude.isSlotFilled('mainMenuCustomContent');
    }

    @bind
    onStick({event}) {
        this.$scope.$apply(() => {
            if (event === 'stick') {
                this.stick();
            } else {
                this.unstick();
            }
        });
    }

    stick() {
        this.isSticked = true;
    }

    unstick() {
        this.isSticked = false;
    }

    checkOverflowing() {
        this.$scope.$broadcast('kp.checkOverflowing');
    }

    searchClick() {
        this.searchActive = true;
    }

    search() {
        this.searchInputService.triggerSearch(this.searchString);
        this.searchString = '';
        this.searchActive = false;
        return false;
    }

    userMenuCheckAuth(event, menuItem) {
        if (menuItem.requiresAuth) {
            if (!this.mainMenuService.isLogged()) {
                event.preventDefault();
                this.mainMenuService.requireAuthClick(menuItem, event);
            }
        }
    }

    isSearchInputEnabled() {
        return this.searchInputService.isEnabled();
    }
}

export default class MainMenuComponent {
    static componentName = 'kpMainMenu';

    templateUrl = template;
    controller = MainMenuComponentController;
    controllerAs = 'mmc';
    transclude = {
        mainMenuLogo: '?kpMainMenuLogo',
        mainMenuCustomContent: '?kpMainMenuCustomContent'
    };
    bindings = {
        menuItems: '<',
        searchPlaceholder: '@'
    };
}

/**
 * @ngdoc type
 * @name menuItem
 * @typedef {object} menuItem
 *
 * @property {string} text Menu item text
 * @property {Array<menuItem>|null} submenu Submenu
 * @property {boolean|null} requiresAuth If true, on click main menu calls {@link service:mainMenuService#isLogged isLogged} method and based on its
 *                                  return value calls {@link service:mainMenuService#requireAuthClick requireAuthClick} method.
 * @property {string|null} type If type is `'user'`, this menu item will be rendered on right side of menu
 *
 * @description
 * This type defines menu structure
 */
